import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import dayjs from 'dayjs';
import { selectUserActiveEmployerInfo } from 'store/user/selectors.js/user.selectors';
import { patchUserProjectPreference } from '../services/patchUserProjectPreference.service';
import { getProjectsActivityFilterCacheKey } from '../utils/getProjectsActivityFilterCacheKey';
import { getUserProjectPreferenceKey } from '../utils/userProjectPreferences';

export const applyOptimisticUserProjectPreferenceUpdate = createAction(
  'projects/applyOptimisticUserProjectPreferenceUpdate'
);

export const updateUserProjectPreference = createAsyncThunk(
  'projects/updateUserProjectPreference',
  async (
    {
      project,
      favorite,
      hidden,
      // How the project dashboard shows the sensor readings, a
      // SENSOR_DISPLAY (utils/userProjectPreferences).
      sensorDisplay,
      activeEmployerInfo: overrideActiveEmployerInfo,
    },
    { dispatch, getState, requestId }
  ) => {
    const activeEmployerInfo =
      overrideActiveEmployerInfo || selectUserActiveEmployerInfo(getState());
    const now = dayjs().toISOString();
    // Whether the update moves the project in the project list. The
    // dashboard's sensor display alone does not: the project stays where it
    // is among the latest projects, and among the favourites, which are
    // ordered by updatedAt.
    const movesInProjectList = favorite !== undefined || hidden !== undefined;
    // Only the choices the update makes; the preference keeps the rest.
    const preference = {
      businessId: project.business_id,
      projectId: project.id,
      ...(favorite !== undefined ? { favorite } : {}),
      ...(hidden !== undefined ? { hidden } : {}),
      ...(sensorDisplay !== undefined ? { sensorDisplay } : {}),
      entityType: 'USER_PROJECT_PREFERENCE',
    };

    const existingPreference =
      getState().projects.userProjectPreferences?.[
        getUserProjectPreferenceKey(preference)
      ];

    const mergedPreference = {
      ...existingPreference,
      ...preference,
      createdAt: existingPreference?.createdAt || now,
      updatedAt: (!movesInProjectList && existingPreference?.updatedAt) || now,
    };

    const cacheKey = movesInProjectList
      ? getProjectsActivityFilterCacheKey(activeEmployerInfo)
      : null;
    const latestProjectIds =
      (cacheKey &&
        getState().projects.latestUserActiveProjectIdsByEmployer?.[cacheKey]) ||
      [];

    dispatch(
      applyOptimisticUserProjectPreferenceUpdate({
        requestId,
        cacheKey,
        previousPreference: existingPreference || null,
        previousLatestProjectIds: [...latestProjectIds],
        preference: mergedPreference,
      })
    );

    return patchUserProjectPreference({
      preference: mergedPreference,
      activeEmployerInfo,
    });
  }
);
